import React, { useEffect, useState, useContext, useCallback } from "react";
import axios from "axios";
import { Tab, Tabs } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";

import { checkAccessList } from "../common/utilities";
import { Loader } from "../components/customComponents";
import { utilities } from "../components/utilities";

import {
  DELETE_TRAINING,
  GET_COURSE_STATIC_DATA,
  GET_GOAL_STATIC_DATA,
  UPCOMING_TRAININGS,
} from "../constants/urlConstants";

import AddTraining from "./AddTraining";
import TrainingHistory from "./TrainingHistory";
import TrainingSummary from "./TrainingSummary";
import UploadParticipants from "./UploadParticipants";
import UploadQuestionnaires from "./UploadQuestionnaires";
import TrainingGoals from "./TrainingGoals";

import {
  prepareContentUrl,
  prepareListUrl,
  README_FILE_NAME,
  TRAINING_PROJECT,
} from "../constants/oneDriveConstants";

import { getListFromOneDrive, getFileFromOneDrive } from "../Office365/Office365Graph";
import { appContext } from "../Office365/Office365Context";

const Trainings = () => {
  const [courses, setCourses] = useState([]);
  const [staticData, setStaticData] = useState([]);
  const [competencyStaticData, setCompetencyStaticData] = useState([]);
  const [dropDownData, setDropDownData] = useState(null);
  const [courseData, setCourseData] = useState(null);

  // Separate loading flags
  const [isCoursesLoading, setIsCoursesLoading] = useState(false);
  const [isOneDriveLoading, setIsOneDriveLoading] = useState(false);

  const [enRollments] = useState([]);
  const suggestions = useSelector((state) => state.EmployeeListReducer.employeeList);
  const isAdmin = useSelector((state) => state.CurrentUserReducer.currentUserDetail.isAdmin);

  const history = useHistory();
  const match = useRouteMatch();
  const { authProvider } = useContext(appContext);

  // ---------- Unified error handler for Axios + Graph ----------
  const handleApiError = (error, defaultMessage) => {
    // Axios
    if (axios.isAxiosError?.(error)) {
      if (error.response) {
        console.error("Axios Error Response:", error.response);
        utilities.showToast(
          error.response.data?.message ||
            error.response.data?.error ||
            `${defaultMessage} (Code: ${error.response.status})`
        );
      } else if (error.request) {
        console.error("Axios Error Request:", error.request);
        utilities.showToast(`${defaultMessage}: No response from server`);
      } else {
        console.error("Axios Error:", error.message);
        utilities.showToast(`${defaultMessage}: ${error.message}`);
      }
      return;
    }

    // Microsoft Graph / generic
    const graphMessage =
      error?.body?.error?.message ||
      error?.message ||
      error?.error?.message ||
      "Unexpected error";
    const status = error?.statusCode || error?.code;

    console.error("Non-Axios Error:", error);
    utilities.showToast(
      status ? `${defaultMessage} (Code: ${status}): ${graphMessage}` : `${defaultMessage}: ${graphMessage}`
    );
  };

  // ---------- API calls (Axios) ----------
  const fetchCourses = useCallback(async () => {
    try {
      setIsCoursesLoading(true);
      const { data } = await axios.get(UPCOMING_TRAININGS);
      setCourses(Array.isArray(data) ? data : []);
    } catch (error) {
      handleApiError(error, "Failed to fetch courses");
      setCourses((prev) => prev ?? []);
    } finally {
      setIsCoursesLoading(false);
    }
  }, []);

  const deleteTraining = async (dataToDelete) => {
    try {
      await axios.delete(DELETE_TRAINING, {
        data: { training_id: dataToDelete.training_id },
      });
      fetchCourses();
    } catch (error) {
      handleApiError(error, "Failed to delete training");
    }
  };

  const fetchStaticData = async () => {
    try {
      const { data } = await axios.get(GET_COURSE_STATIC_DATA);
      if (data?.errorType) {
        setStaticData([]);
        utilities.showToast(data.errorMessage || "Failed to fetch static data");
        return;
      }
      setStaticData(Array.isArray(data) ? data : []);
    } catch (error) {
      handleApiError(error, "Failed to fetch static data");
      setStaticData((prev) => prev ?? []);
    }
  };

  const fetchCompetencyStaticData = async () => {
    try {
      const { data } = await axios.get(GET_GOAL_STATIC_DATA);
      setCompetencyStaticData(Array.isArray(data) ? data : []);
    } catch (error) {
      handleApiError(error, "Failed to fetch competency data");
      setCompetencyStaticData((prev) => prev ?? []);
    }
  };

  // ---------- OneDrive (Graph) ----------
  const fetchOneDriveData = async () => {
    if (!authProvider) return;
    try {
      setIsOneDriveLoading(true);

      // Readme / training history
      const readmePath = prepareContentUrl(TRAINING_PROJECT, README_FILE_NAME);
      const fileResp = await getFileFromOneDrive(authProvider, readmePath);
      setCourseData(fileResp?.training_history_data ?? []);

      // Dropdown list
      const listUrl = prepareListUrl(TRAINING_PROJECT);
      const listResp = await getListFromOneDrive(authProvider, listUrl);
      const filtered = (listResp?.value ?? []).filter(
        (item) => !item.name.includes(".yml") && !item.name.includes(".json")
      );
      setDropDownData(filtered);
    } catch (error) {
      handleApiError(error, "Failed to fetch OneDrive data");
      setCourseData((prev) => prev ?? []);
      setDropDownData((prev) => prev ?? []);
    } finally {
      setIsOneDriveLoading(false);
    }
  };

  // ---------- Local helpers ----------
  const changeCourseStatus = (courseDetail) => {
    setCourses((prevCourses) =>
      prevCourses.map((course) =>
        course.training_id === courseDetail.training_id
          ? { ...course, is_enrollment_open: course.is_enrollment_open === 0 ? 1 : 0 }
          : course
      )
    );
  };

  const getDefaultTabKey = () => {
    const seg = match?.params?.[0];
    if (!seg) return "upcoming";
    if (seg.includes("/")) {
      return seg.split("/").includes("completed") ? "completed/32323986" : "upcoming";
    }
    return seg.toLowerCase() === "completed" ? "completed/32323986" : "upcoming";
  };

  // ---------- Effects ----------
  useEffect(() => {
    fetchCourses();
    fetchStaticData();
    fetchCompetencyStaticData();
  }, [fetchCourses]);

  useEffect(() => {
    fetchOneDriveData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authProvider]);

  // ---------- Render ----------
  const renderUpcomingTrainings = () => (
    <div className="column-flex">
      {checkAccessList("crud_train") && (
        <div className="addIcon">
          <AddTraining
            suggestions={suggestions}
            fetchCourses={fetchCourses}
            staticValues={staticData}
          />
        </div>
      )}

      {isCoursesLoading ? (
        <Loader />
      ) : courses.length > 0 ? (
        <div className="row">
          {courses.map((course) => (
            <TrainingSummary
              isAdmin={isAdmin}
              topic={course}
              key={course.training_id ?? course.id}
              enRollments={enRollments}
              deleteTraining={deleteTraining}
              fetchCourses={fetchCourses}
              changeCourseStatus={changeCourseStatus}
              staticValues={staticData}
            />
          ))}
        </div>
      ) : (
        <center>
          <p className="no_data">No Upcoming Trainings to Show !!</p>
        </center>
      )}
    </div>
  );

  const oneDriveReady = !!courseData && !!dropDownData;

  return (
    <div className="page-wrap">
      <Tabs
        defaultActiveKey={getDefaultTabKey()}
        id="trainings-tab"
        onSelect={(e) => history.push(`/trainings/${e}`)}
      >
        <Tab eventKey="upcoming" title="UPCOMING TRAININGS">
          {renderUpcomingTrainings()}
        </Tab>

        <Tab eventKey="completed/32323986" title="TRAINING HISTORY">
          {isOneDriveLoading && !oneDriveReady ? (
            <Loader />
          ) : (
            <TrainingHistory readmeData={courseData ?? []} oneDriveData={dropDownData ?? []} />
          )}
        </Tab>

        {checkAccessList("upload_quest") && (
          <Tab eventKey="upload" title="UPLOAD QUESTIONNAIRES">
            {isOneDriveLoading && !oneDriveReady ? (
              <Loader />
            ) : (
              <UploadQuestionnaires dropDownData={dropDownData ?? []} readmeData={courseData ?? []} />
            )}
          </Tab>
        )}

        {checkAccessList("upload_attendance") && (
          <Tab eventKey="participants" title="TRAINING ATTENDANCE">
            {isOneDriveLoading && !oneDriveReady ? (
              <Loader />
            ) : (
              <UploadParticipants dropDownData={dropDownData ?? []} readmeData={courseData ?? []} />
            )}
          </Tab>
        )}

        {checkAccessList("training_goals") && (
          <Tab eventKey="training-goals" title="TRAINING GOALS">
            <TrainingGoals staticValues={competencyStaticData} trainings={courseData ?? []} />
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

export default Trainings;
